/**
 * Tab "Contract Detail" — rebuild 1:1 theo Access `frmProcurementMainSubFormDetailsP`
 * / `…DetailsNPE`:
 *
 *   ┌ Header (Card): 2 picker P/NPE + grid field 3 cột × 8 hàng (ContractDetailHeader) ┐
 *   ├ Nav buttons: P = [Pay Info]; NPE = [Pay Info][Advance][Fixation]                 ┤
 *   └ Body: PayInfo (3 grid GoodsAlc/Paydata/Invoice) | Advance | Fixation             ┘
 *
 * Chọn picker P → variant P (Access đổi SourceObject navProcurement); chọn picker NPE
 * → variant NPE. PERSIST theo user (skill /user-logaction): `variant` + contract_no
 * từng variant ở `user_preferences` scope `purchase` / key `contract_detail`.
 *
 * Contract Detail là màn chi tiết 1 hợp đồng được chọn (Access DefaultView=0) — KHÔNG
 * chịu global Delivery-Latest filter của header Procurement.
 *
 * Action wiring (Access parity):
 *   - Tab1 "Write data": P+Outright → AddPayOutrightDialog; NPE → PayDataFormDialog
 *     (Advanced Add); guard forced-close / "Please choose Outright Contract!".
 *   - Tab1 "Write data PTBF": P+Differential → AddPayPTBFDialog; NPE → snack
 *     "No write data PTBF for NPE contract!".
 *   - Tab1 Invoice "Write data": AddInvoiceDialog (guard forced-close cho P).
 *   - Tab2 New/Copy/Edit → PayDataFormDialog; Delete → guard BE; Print → Pay Confirm.
 *   - Tab3 Edit → NPEFixationFormDialog; Delete → BE 4 bảng; Download → proxy excel;
 *     Print → NPEFixationPrintDialog; Add Pay → PayDataFormDialog (prefill P contract).
 */
import { Backdrop, Box, CircularProgress } from '@mui/material'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'

import BrandState from '../../components/BrandState'
import ConfirmDialog from '../../components/ConfirmDialog'
import { API_BASE_URL } from '@/config/api'
import { usePagePermission } from '../../hooks/usePagePermission'
import { usePageFilters } from '../../hooks/usePageFilters'
import { usePreference } from '../../hooks/usePreference'
import { useSnackbar } from '../../hooks/useSnackbar'
import { getStoredToken } from '@/services/auth'
import {
  invalidateContractDetailLists,
  useDeleteContractAdvanceMutation,
  useDeleteContractFixationMutation,
  useGetContractHeaderNPEQuery,
  useGetContractHeaderPQuery,
  useGetContractPicksQuery,
  type AdvanceRow,
  type FixationRow,
} from '../../store/api/procurementApi'
import { useLazyGetNPEFixationQuery } from '@/store/api/purchaseContractApi'
import type { NPEFixationOut } from '@/types/api'
import { getErrorMessage } from '@/utils/error'
import { NPEFixationFormDialog } from '../purchase/NPEFixationFormDialog'
import { NPEFixationPrintDialog, type NPEPrintTarget } from '../purchase/NPEFixationListPage'
import ContractDetailAddInvoiceDialog from './ContractDetailAddInvoiceDialog'
import ContractDetailAddPayOutrightDialog from './ContractDetailAddPayOutrightDialog'
import ContractDetailAddPayPTBFDialog from './ContractDetailAddPayPTBFDialog'
import ContractDetailAdvance from './ContractDetailAdvance'
import ContractDetailFixation from './ContractDetailFixation'
import ContractDetailHeader, { type ContractVariant } from './ContractDetailHeader'
import ContractDetailPayInfo from './ContractDetailPayInfo'
import PayDataConfirmPrintDialog from './PayDataConfirmPrintDialog'
import PayDataFormDialog from './PayDataFormDialog'

type DetailMode = 'payinfo' | 'advance' | 'fixation'

interface ContractDetailPref {
  variant: ContractVariant
  npeContractNo: string | null
  pContractNo: string | null
  mode: DetailMode
  reqFx: string
}

const NPE_MODES: Array<{ key: DetailMode; label: string }> = [
  { key: 'payinfo', label: 'Pay Info' },
  { key: 'advance', label: 'Advance' },
  { key: 'fixation', label: 'Fixation' },
]

interface PayDataDialogState {
  open: boolean
  mode: 'new' | 'edit' | 'copy'
  paydataId: number | null
  initialContractNo: string | null
}

interface ConfirmState {
  kind: 'advance' | 'fixation'
  id: number
  message: string
}

export default function ProcurementContractDetailView() {
  const pref = usePreference<ContractDetailPref>('procurement', 'contract_detail')
  const snack = useSnackbar()
  const dispatch = useDispatch()
  const { canEdit } = usePagePermission()

  const [variant, setVariant] = useState<ContractVariant>('npe')
  const [pContractNo, setPContractNo] = useState<string | null>(null)
  const [npeContractNo, setNpeContractNo] = useState<string | null>(null)
  const [mode, setMode] = useState<DetailMode>('payinfo')
  const [reqFx, setReqFx] = useState('')

  // Hydrate 1 lần từ preference khi load Page.
  const hydrated = useRef(false)
  useEffect(() => {
    if (pref.isLoading || hydrated.current) return
    hydrated.current = true
    setVariant(pref.value.variant ?? 'npe')
    setPContractNo(pref.value.pContractNo ?? null)
    setNpeContractNo(pref.value.npeContractNo ?? null)
    setMode(pref.value.mode ?? 'payinfo')
    setReqFx(pref.value.reqFx ?? '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pref.isLoading])

  // Debounced persist on change (delta-only patch).
  useEffect(() => {
    if (!hydrated.current) return
    const t = setTimeout(() => {
      void pref.patch({ variant, npeContractNo, pContractNo, mode, reqFx }).catch(() => undefined)
    }, 450)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant, npeContractNo, pContractNo, mode, reqFx])

  const picks = useGetContractPicksQuery()
  // ChooseContract list lọc theo khoảng Delivery Latest (filter global của trang Procurement).
  const { filters: procFilters } = usePageFilters<{ startDate?: string; endDate?: string }>('procurement')
  const filteredPicks = useMemo(() => {
    const data = picks.data
    if (!data) return data
    const from = procFilters?.startDate
    const to = procFilters?.endDate
    if (!from && !to) return data
    // Guard: BE chưa trả latest_delivery (chưa restart) → không lọc để tránh dropdown rỗng.
    const hasField = data.p.some((it) => it.latest_delivery != null) || data.npe.some((it) => it.latest_delivery != null)
    if (!hasField) return data
    const inRange = (d: string | null | undefined) => {
      if (!d) return false
      const day = String(d).slice(0, 10)
      if (from && day < from) return false
      if (to && day > to) return false
      return true
    }
    return {
      p: data.p.filter((it) => inRange(it.latest_delivery)),
      npe: data.npe.filter((it) => inRange(it.latest_delivery)),
    }
  }, [picks.data, procFilters?.startDate, procFilters?.endDate])
  const headerP = useGetContractHeaderPQuery(
    { contractNo: pContractNo ?? '' },
    { skip: variant !== 'p' || !pContractNo },
  )
  const headerNPE = useGetContractHeaderNPEQuery(
    { contractNo: npeContractNo ?? '' },
    { skip: variant !== 'npe' || !npeContractNo },
  )

  const contractNo = variant === 'p' ? pContractNo : npeContractNo
  const isForcedClose = variant === 'p' && Number(headerP.data?.is_forced_close_int ?? 0) !== 0
  const contractType = String(headerP.data?.contract_type ?? '')

  // Contract Type + Contract List (liên động): mỗi loại GIỮ contract riêng của nó
  // (đổi Type qua lại vẫn nhớ contract đã chọn từng bên → persist theo user).
  const handleSelectType = (v: ContractVariant) => {
    setVariant(v)
    setMode('payinfo')
  }
  const handlePickP = (value: string | null) => {
    setPContractNo(value)
    setVariant('p')
    setMode('payinfo')
  }
  const handlePickNPE = (value: string | null) => {
    setNpeContractNo(value)
    setVariant('npe')
    setMode('payinfo')
  }

  const refreshLists = () => {
    dispatch(invalidateContractDetailLists())
  }

  // ---- PayDataFormDialog (Advanced Add / Add Pay / Tab2 New-Copy-Edit) ----
  const [payDataDialog, setPayDataDialog] = useState<PayDataDialogState>({
    open: false,
    mode: 'new',
    paydataId: null,
    initialContractNo: null,
  })
  const closePayDataDialog = () => setPayDataDialog((p) => ({ ...p, open: false }))

  // ---- Pay Confirm print (Tab2 Print) ----
  const [printPaydata, setPrintPaydata] = useState<{ id: number; xntt: string | null; paymentType: string | null; contractNo: string | null } | null>(null)

  // ---- Fixation edit / print ----
  const [fetchFixationDetail] = useLazyGetNPEFixationQuery()
  const [fixEdit, setFixEdit] = useState<NPEFixationOut | null>(null)
  const [fixEditOpen, setFixEditOpen] = useState(false)
  const [fixEditLoading, setFixEditLoading] = useState(false)
  const [fixPrint, setFixPrint] = useState<NPEPrintTarget | null>(null)

  // ---- Delete confirms ----
  const [confirm, setConfirm] = useState<ConfirmState | null>(null)
  const [deleteAdvance, { isLoading: deletingAdvance }] = useDeleteContractAdvanceMutation()
  const [deleteFixation, { isLoading: deletingFixation }] = useDeleteContractFixationMutation()

  const runConfirmedDelete = async () => {
    if (!confirm) return
    try {
      if (confirm.kind === 'advance') {
        await deleteAdvance({ paydataId: confirm.id }).unwrap()
      } else {
        await deleteFixation({ fixationId: confirm.id }).unwrap()
      }
      snack.success('Successfully Deleted')
      setConfirm(null)
    } catch (err) {
      snack.error(getErrorMessage(err, 'Delete failed'))
      setConfirm(null)
    }
  }

  // ---- Download File (Tab3) — proxy BE, tải blob với Bearer token ----
  const handleDownloadFixation = async (row: FixationRow) => {
    const id = row.cs_fixation_id
    if (id == null) return
    try {
      const resp = await fetch(
        `${API_BASE_URL}/purchase/contract-detail/fixation/${id}/download`,
        { headers: { Authorization: `Bearer ${getStoredToken() ?? ''}` } },
      )
      if (!resp.ok) {
        let detail = 'Download failed.'
        try {
          detail = String((await resp.json())?.detail ?? detail)
        } catch {
          /* keep default */
        }
        snack.error(detail)
        return
      }
      const blob = await resp.blob()
      const disp = resp.headers.get('Content-Disposition') ?? ''
      const star = /filename\*\s*=\s*([^;]+)/i.exec(disp)
      let filename = `fixation_${row.fixation_no ?? id}.xlsx`
      if (star?.[1]) {
        const v = star[1].trim().replace(/^UTF-8''/i, '').replace(/^"|"$/g, '')
        try {
          filename = decodeURIComponent(v)
        } catch {
          filename = v
        }
      }
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
    } catch (err) {
      snack.error(getErrorMessage(err, 'Download failed.'))
    }
  }

  // ---- Tab1 Write buttons (Access btnPayDataOutright / btnPayDataPTBF / btnInvoiceTLC) ----
  const [outrightDialog, setOutrightDialog] = useState<{ open: boolean; requestedFx: string }>({
    open: false,
    requestedFx: '',
  })

  const handleWriteOutright = (requestedFx: string) => {
    if (!contractNo) {
      snack.error('Please choose ContractNo!')
      return
    }
    if (variant === 'npe') {
      // Access: NPE → mở Advanced Add.
      setPayDataDialog({ open: true, mode: 'new', paydataId: null, initialContractNo: contractNo })
      return
    }
    if (isForcedClose) {
      snack.error('This contract is forced close!')
      return
    }
    if (contractType !== 'Outright') {
      snack.error('Please choose Outright Contract!')
      return
    }
    setOutrightDialog({ open: true, requestedFx })
  }

  const [ptbfDialog, setPtbfDialog] = useState<{ open: boolean; requestedFx: string }>({
    open: false,
    requestedFx: '',
  })

  const handleWritePTBF = (requestedFx: string) => {
    if (!contractNo) {
      snack.error('Please choose ContractNo!')
      return
    }
    if (variant === 'npe') {
      snack.error('No write data PTBF for NPE contract!')
      return
    }
    if (isForcedClose) {
      snack.error('This contract is forced close!')
      return
    }
    if (contractType === 'Outright') {
      snack.error('Please choose Differential Contract!')
      return
    }
    setPtbfDialog({ open: true, requestedFx })
  }

  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false)
  const handleWriteInvoice = () => {
    if (!contractNo) {
      snack.error('Please choose ContractNo!')
      return
    }
    if (isForcedClose) {
      snack.error('This contract is forced close!')
      return
    }
    setInvoiceDialogOpen(true)
  }
  const headerContractDate =
    variant === 'p'
      ? ((headerP.data?.contract_date as string | null) ?? null)
      : ((headerNPE.data?.contract_date as string | null) ?? null)

  // ---- Tab2 toolbar (Advance) ----
  const openAdvanceNew = () => {
    if (!contractNo) return
    setPayDataDialog({ open: true, mode: 'new', paydataId: null, initialContractNo: contractNo })
  }
  const openAdvanceCopy = (row: AdvanceRow) =>
    setPayDataDialog({
      open: true,
      mode: 'copy',
      paydataId: row.paydata_p_id ?? null,
      initialContractNo: null,
    })
  const openAdvanceEdit = (row: AdvanceRow) =>
    setPayDataDialog({
      open: true,
      mode: 'edit',
      paydataId: row.paydata_p_id ?? null,
      initialContractNo: null,
    })
  const confirmDeleteAdvance = (row: AdvanceRow) => {
    if (row.paydata_p_id == null) return
    setConfirm({
      kind: 'advance',
      id: row.paydata_p_id,
      message: `Do you want to delete Advanced ID = '${row.paydata_p_id}'?`,
    })
  }
  const printAdvance = (row: AdvanceRow) => {
    if (row.paydata_p_id == null) return
    if (!String(row.xntt_no ?? '').trim()) {
      snack.error('XNTT No is Null!')
      return
    }
    setPrintPaydata({ id: row.paydata_p_id, xntt: row.xntt_no ?? null, paymentType: row.payment_type, contractNo: row.contract_no })
  }

  // ---- Tab3 toolbar (Fixation) ----
  const openFixationEdit = async (row: FixationRow) => {
    if (row.cs_fixation_id == null) return
    // Bật loading NGAY khi bấm (tránh click chết trong lúc fetch) rồi mới fetch.
    setFixEditLoading(true)
    try {
      const res = await fetchFixationDetail(row.cs_fixation_id).unwrap()
      setFixEdit(res.data)
      setFixEditOpen(true)
    } catch (err) {
      snack.error(getErrorMessage(err, 'Failed to load fixation.'))
    } finally {
      setFixEditLoading(false)
    }
  }
  const confirmDeleteFixation = (row: FixationRow) => {
    if (row.cs_fixation_id == null) return
    setConfirm({
      kind: 'fixation',
      id: row.cs_fixation_id,
      message:
        `Do you want to delete Fixation '${row.fixation_no ?? row.cs_fixation_id}'? ` +
        'This also removes its P contract, allocations and payments.',
    })
  }
  const printFixation = (row: FixationRow) => {
    if (row.cs_fixation_id == null) return
    setFixPrint({ fixationId: row.cs_fixation_id, fixationNo: String(row.fixation_no ?? '') })
  }
  const addPayFromFixation = (row: FixationRow) => {
    const pcn = String(row.p_contract_no ?? '').trim()
    if (!pcn) {
      snack.error('This fixation has no P contract.')
      return
    }
    setPayDataDialog({ open: true, mode: 'new', paydataId: null, initialContractNo: pcn })
  }

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
      <ContractDetailHeader
        variant={variant}
        picks={filteredPicks}
        picksLoading={picks.isLoading || picks.isFetching}
        pContractNo={pContractNo}
        npeContractNo={npeContractNo}
        onPickP={handlePickP}
        onPickNPE={handlePickNPE}
        onSelectType={handleSelectType}
        mode={mode}
        modeOptions={(variant === 'npe' ? NPE_MODES : NPE_MODES.slice(0, 1)).map((m) => ({
          value: m.key,
          label: m.label,
        }))}
        onModeChange={(m) => setMode(m as DetailMode)}
        headerP={variant === 'p' ? headerP.data ?? null : null}
        headerNPE={variant === 'npe' ? headerNPE.data ?? null : null}
      />

      {!contractNo ? (
        <BrandState
          tone="empty"
          title="Select a contract"
          description="Pick a P Contract or NPE Contract above to view its details."
        />
      ) : mode === 'advance' && variant === 'npe' ? (
        <ContractDetailAdvance
          contractNo={contractNo}
          canEdit={canEdit && !isForcedClose}
          onNew={openAdvanceNew}
          onCopy={openAdvanceCopy}
          onEdit={openAdvanceEdit}
          onDelete={confirmDeleteAdvance}
          onPrint={printAdvance}
        />
      ) : mode === 'fixation' && variant === 'npe' ? (
        <ContractDetailFixation
          contractNo={contractNo}
          canEdit={canEdit && !isForcedClose}
          onEdit={(row) => void openFixationEdit(row)}
          onDelete={confirmDeleteFixation}
          onDownload={(row) => void handleDownloadFixation(row)}
          onPrint={printFixation}
          onAddPay={addPayFromFixation}
        />
      ) : (
        <ContractDetailPayInfo
          variant={variant}
          contractNo={contractNo}
          contractType={contractType}
          fixLevel={variant === 'p' ? headerP.data?.fix_level ?? null : null}
          fixedFx={variant === 'p' ? headerP.data?.fixed_fx ?? null : null}
          reqFx={reqFx}
          onReqFxChange={setReqFx}
          onWriteOutright={handleWriteOutright}
          onWritePTBF={handleWritePTBF}
          onWriteInvoice={handleWriteInvoice}
          disabled={isForcedClose}
        />
      )}

      {/* ---- Dialogs ---- */}
      {contractNo ? (
        <ContractDetailAddInvoiceDialog
          open={invoiceDialogOpen}
          contractNo={contractNo}
          contractDate={headerContractDate}
          onClose={() => setInvoiceDialogOpen(false)}
          onSaved={refreshLists}
        />
      ) : null}

      {contractNo ? (
        <ContractDetailAddPayOutrightDialog
          open={outrightDialog.open}
          contractNo={contractNo}
          requestedFx={outrightDialog.requestedFx}
          onClose={() => setOutrightDialog((p) => ({ ...p, open: false }))}
          onSaved={refreshLists}
        />
      ) : null}

      {contractNo ? (
        <ContractDetailAddPayPTBFDialog
          open={ptbfDialog.open}
          contractNo={contractNo}
          requestedFx={ptbfDialog.requestedFx}
          fixLevel={variant === 'p' ? headerP.data?.fix_level ?? null : null}
          onClose={() => setPtbfDialog((p) => ({ ...p, open: false }))}
          onSaved={refreshLists}
        />
      ) : null}

      <PayDataFormDialog
        open={payDataDialog.open}
        mode={payDataDialog.mode}
        paydataId={payDataDialog.paydataId}
        canEdit={canEdit}
        initialContractNo={payDataDialog.initialContractNo}
        onClose={closePayDataDialog}
        onSaved={() => {
          closePayDataDialog()
          refreshLists()
        }}
      />

      <PayDataConfirmPrintDialog
        open={printPaydata !== null}
        paydataId={printPaydata?.id ?? null}
        xnttNo={printPaydata?.xntt ?? null}
        paymentType={printPaydata?.paymentType ?? null}
        contractNo={printPaydata?.contractNo ?? null}
        onClose={() => setPrintPaydata(null)}
      />

      <NPEFixationFormDialog
        open={fixEditOpen}
        mode="edit"
        initialValue={fixEdit}
        onClose={() => setFixEditOpen(false)}
        onSaved={() => {
          setFixEditOpen(false)
          refreshLists()
        }}
      />

      <NPEFixationPrintDialog
        open={fixPrint !== null}
        printTarget={fixPrint}
        canEdit={canEdit}
        onClose={() => setFixPrint(null)}
      />

      <ConfirmDialog
        open={confirm !== null}
        title={confirm?.kind === 'advance' ? 'Delete Advance' : 'Delete Fixation'}
        message={confirm?.message ?? ''}
        danger
        loading={deletingAdvance || deletingFixation}
        onConfirm={() => void runConfirmedDelete()}
        onClose={() => setConfirm(null)}
      />

      {/* Overlay spinner khi đang fetch fixation detail để mở dialog Edit. */}
      <Backdrop open={fixEditLoading} sx={{ zIndex: (t) => t.zIndex.modal + 1, color: '#fff' }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  )
}
